import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Card } from '@/components/ui/card';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { cn } from '@/lib/utils';
import { StoreListingReviewQueue } from './StoreListingReviewQueue';

function severityChip(count: number, severity: 'critical' | 'high'): string {
  if (count === 0) return 'text-muted-foreground';
  if (severity === 'critical') return 'text-red-700 dark:text-red-400 font-semibold';
  return 'text-orange-700 dark:text-orange-400 font-semibold';
}

function ageString(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AdminReviewQueue() {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.escalated(),
    queryFn: () => api.listEscalatedPluginReviews({ limit: 50 }),
  });

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dev Console
        </Link>
      </div>

      <div className="mb-8 flex items-start gap-4">
        <ShieldAlert className="h-8 w-8 text-amber-500 mt-1" />
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">Plugin Review Queue</h1>
            <NotificationBadge count={data?.total ?? 0} aria-label="Arches awaiting review" />
          </div>
          <p className="mt-2 text-muted-foreground">
            Submissions the AI escalated for human review. Approve to release the build, or reject
            with notes that the contributor will see.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading queue...</p>}
      {error && <p className="text-destructive">Failed to load: {(error as Error).message}</p>}

      {data && data.reviews.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Nothing in the queue right now.</p>
        </Card>
      )}

      {data && data.reviews.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 text-left text-sm">
              <tr>
                <th className="px-4 py-3 font-medium">Arche</th>
                <th className="px-4 py-3 font-medium">Submitted by</th>
                <th className="px-4 py-3 font-medium">Risk</th>
                <th className="px-4 py-3 font-medium">Critical</th>
                <th className="px-4 py-3 font-medium">High</th>
                <th className="px-4 py-3 font-medium">Files</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {data.reviews.map((r) => (
                <tr key={r.reviewId} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.manifestName}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">
                      {r.submissionId}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {/* Who actually pushed this code. On a multi-collaborator
                        plugin that is not necessarily the arche's owner, so the
                        id stays visible under the name for the audit trail. */}
                    <div className="font-medium">{r.submittedByUsername ?? 'Unknown user'}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">
                      {r.submittedBy}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        r.riskScore && r.riskScore >= 70
                          ? 'text-amber-700 dark:text-amber-400 font-semibold'
                          : '',
                      )}
                    >
                      {r.riskScore ?? '—'}
                    </span>
                  </td>
                  <td className={cn('px-4 py-3', severityChip(r.criticalCount, 'critical'))}>
                    {r.criticalCount}
                  </td>
                  <td className={cn('px-4 py-3', severityChip(r.highCount, 'high'))}>
                    {r.highCount}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.filesAnalyzed}</td>
                  <td className="px-4 py-3 text-muted-foreground">{ageString(r.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/submissions/${r.submissionId}`}
                      className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90"
                    >
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <StoreListingReviewQueue />
    </div>
  );
}
